// Utilities
import { defineStore } from 'pinia'
import { useTenantsStore } from './tenants'
import { useBillingStore } from './billing'
import { TenantPlan, isCreditPlan, type TenantPlanFeatures } from '@/models/Tenant'
import { useUsersStore } from './users'
import { useProjectsStore } from './projects'
import { ProjectCreationRequestType } from '@/models/Billing'

const LEGACY_PLAN_FEATURES: Record<string, TenantPlanFeatures> = {
    [TenantPlan.EVALUATOR]: { has_qms: true, has_td: false, has_veille: true, has_chat: true, max_users: 10, max_projects_qms: Infinity, max_projects_td: 0, credits_monthly: 0, welcome_bonus_eligible: false },
    [TenantPlan.MANAGER]: { has_qms: true, has_td: true, has_veille: true, has_chat: true, max_users: 10, max_projects_qms: Infinity, max_projects_td: Infinity, credits_monthly: 0, welcome_bonus_eligible: false },
    [TenantPlan.EARLY_ACCESS]: { has_qms: true, has_td: false, has_veille: false, has_chat: true, max_users: 10, max_projects_qms: Infinity, max_projects_td: 0, credits_monthly: 0, welcome_bonus_eligible: false },
    [TenantPlan.FREE]: { has_qms: true, has_td: false, has_veille: false, has_chat: true, max_users: 1, max_projects_qms: Infinity, max_projects_td: 0, credits_monthly: 0, welcome_bonus_eligible: false },
    [TenantPlan.FREE_TRY]: { has_qms: true, has_td: false, has_veille: false, has_chat: true, max_users: 1, max_projects_qms: Infinity, max_projects_td: 0, credits_monthly: 0, welcome_bonus_eligible: false },
    [TenantPlan.STUDENT]: { has_qms: true, has_td: false, has_veille: false, has_chat: true, max_users: 1, max_projects_qms: Infinity, max_projects_td: 0, credits_monthly: 0, welcome_bonus_eligible: false },
}

/**
 * Whether the signed-in user is in `uids`. The role getters below are
 * rendered by the header on every page, and `logout()` clears the user before
 * the page reloads: read through the throwing `uid` getter, that last render
 * logged "User not set" on every sign-out (MYAC-1698). Signed out, the user
 * holds no role.
 */
function holdsRole(uids: string[] | undefined): boolean {
    const uid = useUsersStore().user?.uid
    return !!uid && (uids?.includes(uid) ?? false)
}

export const useFeaturesStore = defineStore('features', {
    state: () => ({
    }),
    getters: {
        isFreePlan(): boolean {
            const tenantsStore = useTenantsStore()
            const tenant = tenantsStore.currentTenant
            return tenant?.plan === TenantPlan.FREE
        },
        isTryPlan(): boolean {
            const tenantsStore = useTenantsStore()
            const tenant = tenantsStore.currentTenant
            return tenant?.plan === TenantPlan.FREE_TRY
        },
        isEarlyAccessPlan(): boolean {
            const tenantsStore = useTenantsStore()
            const tenant = tenantsStore.currentTenant
            return tenant?.plan === TenantPlan.EARLY_ACCESS
        },
        isEvaluatorPlan(): boolean {
            const tenantsStore = useTenantsStore()
            const tenant = tenantsStore.currentTenant
            return tenant?.plan === TenantPlan.EVALUATOR
        },
        isStudentPlan(): boolean {
            const tenantsStore = useTenantsStore()
            const tenant = tenantsStore.currentTenant
            return tenant?.plan === TenantPlan.STUDENT
        },
        isManagerPlan(): boolean {
            const tenantsStore = useTenantsStore()
            const tenant = tenantsStore.currentTenant
            return tenant?.plan === TenantPlan.MANAGER
        },
        isCreditPlan(): boolean {
            const tenantsStore = useTenantsStore()
            const tenant = tenantsStore.currentTenant
            return isCreditPlan(tenant?.plan ?? null)
        },
        isCreditsBlocked(): boolean {
            if (!this.isCreditPlan) return false
            // Same rule as every credit gate (`hasAtLeastOneCredit`): blocked
            // below one whole credit, never while the balance is still loading.
            const billingStore = useBillingStore()
            return billingStore.creditBalance !== null && !billingStore.hasAtLeastOneCredit
        },
        isTenantAdmin(): boolean {
            const tenantsStore = useTenantsStore()
            const tenant = tenantsStore.currentTenant
            return holdsRole(tenant?.admins)
        },
        isTenantMember(): boolean {
            const tenantsStore = useTenantsStore()
            const tenant = tenantsStore.currentTenant
            return holdsRole(tenant?.members)
        },
        isProjectAdmin(): boolean {
            const projectsStore = useProjectsStore()
            const project = projectsStore.currentProject
            return holdsRole(project?.admins)
        },
        isProjectMember(): boolean {
            const projectsStore = useProjectsStore()
            const project = projectsStore.currentProject
            return holdsRole(project?.members)
        },
        canStartAssessment(): boolean {
            // Credit-plan tenants are gated by the backend credit system, not frontend quotas
            if (this.isCreditPlan) return true
            const tenantsStore = useTenantsStore()
            const tenant = tenantsStore.currentTenant
            const oneTimeAssessments = tenant?.services?.quota_one_time_assessments?.quota_remaining ?? 0
            const recurrentAssessments = tenant?.services?.quota_recurrent_assessments?.quota_remaining ?? 0
            const freeTryAssessments = tenant?.services?.quota_free_try_assessments?.quota_remaining ?? 0
            return oneTimeAssessments > 0 || recurrentAssessments > 0 || freeTryAssessments > 0
        },
        maxUsersReached(): boolean {
            const features = this.effectivePlanFeatures
            if (!features) return false
            const tenantsStore = useTenantsStore()
            const currentCount = tenantsStore.currentTenant?.members?.length ?? 0
            return currentCount >= features.max_users
        },
        canInviteUser(): boolean {
            if (!this.isTenantAdmin) return false
            if (this.isFreePlan || this.isTryPlan || this.isStudentPlan) return false
            return !this.maxUsersReached
        },
        canInviteProjectMember(): boolean {
            // MYAC-1291 — company admin OR project admin, which is what the
            // rules have said since MYAC-381: `member_management_requests`
            // allows `add` on `isProjectAdmin(pid) || isTenantAdminOfProject(pid)`,
            // and the backend applies it without looking at who asked. Gating
            // on project-admin alone made the button stricter than the rule.
            if (!this.isProjectAdmin && !this.isTenantAdmin) return false
            if (this.isFreePlan || this.isTryPlan || this.isStudentPlan) return false
            return !this.maxUsersReached
        },
        effectivePlanFeatures(): TenantPlanFeatures | null {
            const tenantsStore = useTenantsStore()
            const tenant = tenantsStore.currentTenant
            if (!tenant) return null
            if (tenant.plan_features) return tenant.plan_features
            return LEGACY_PLAN_FEATURES[tenant.plan as string] ?? null
        },
        canCreateProjectOfType() {
            return (type: ProjectCreationRequestType): boolean => {
                if (!this.isTenantAdmin) return false
                const features = this.effectivePlanFeatures
                if (!features) return false
                const projectsStore = useProjectsStore()
                // Quotas apply per tenant: count only the active tenant's projects.
                const countByType = projectsStore.tenantProjects.filter(p => p.type === type).length
                if (type === ProjectCreationRequestType.QMS) {
                    return features.has_qms && countByType < features.max_projects_qms
                }
                if (type === ProjectCreationRequestType.TD) {
                    return features.has_td && countByType < features.max_projects_td
                }
                if (type === ProjectCreationRequestType.REGULATORY_MONITORING) {
                    return features.has_veille
                }
                return true
            }
        },
        canCreateProject(): boolean {
            if (!this.isTenantAdmin) return false
            return this.canCreateProjectOfType(ProjectCreationRequestType.QMS)
                || this.canCreateProjectOfType(ProjectCreationRequestType.TD)
                || this.canCreateProjectOfType(ProjectCreationRequestType.REGULATORY_MONITORING)
        },
        canCreateGoogleDriveDatasource(): boolean {
            return this.isProjectAdmin && !this.isTryPlan
        },
        canCreateOffice365Datasource(): boolean {
            return this.isProjectAdmin && !this.isTryPlan
        },
        canUploadFile(): boolean {
            // Note: Document limit enforcement (5 documents for free_try on manual upload datasources) 
            // happens in the upload store actions (uploadFiles/addUploadFiles)
            // This getter only checks basic permissions
            return this.isProjectMember
        },

    },
    actions: {
    }
})
